import { datadogRum } from '@datadog/browser-rum'
import { CarMaintenanceStepWrapper } from '../car-maintenance-step-wrapper/car-maintenance-step-wrapper'
import { useMaintenanceContext } from '../../context/maintenance-context'
import { SummaryItem } from '../summary-item/summary-item'
import { Alert, Text, TextLink } from '@cinch-labs/design-system'
import styles from './summary.module.css'
import {
  EventNames,
  TrackingEventTypes,
  useAnalytics,
} from '@cinch-nx/shared-analytics'
import { useSendServicingEnquiry } from '../../hooks/send-servicing-enquiry'
import { useEffect } from 'react'
import { LoadingSpinner } from '@cinch-nx/shared-ui'
import CinchServicingLogo from '../../assets/cinchServicing-logo.png'
import Image from 'next/image'
import { designTokens } from '@cinch-labs/design-system'
import { useCreateBooking } from '../../hooks/create-booking'
import { format } from 'date-fns'
import { getCampaignIdCookie } from '../../utils/campaign-id'

export const Summary: React.FC = () => {
  const context = useMaintenanceContext()
  const {
    dispatch,
    customerTitle,
    customerFirstName,
    customerSurname,
    customerEmail,
    customerAddress,
    vehicleMake,
    vehicleModel,
    vrm,
    store,
    notes,
    customerPhone,
    postcode,
    step,
    contactTimePreference,
    serviceOptionsSelectedNames,
    serviceOptionsSelectedTotalPrice,
    appointmentType,
    bookingDate,
    bookingTime,
    canBookAppointment,
  } = context

  const { trackEvent } = useAnalytics()

  const { sendServicingEnquiry, loading, enquirySent, error } =
    useSendServicingEnquiry()

  const {
    bookAppointment,
    loading: createBookingLoading,
    bookingCreated,
    error: createBookingError,
  } = useCreateBooking()

  useEffect(() => {
    if (enquirySent || bookingCreated) {
      dispatch({ type: 'SET_ENQUIRY_SUBMITTED' })
    }
  }, [enquirySent, bookingCreated])

  const handleContinue = async () => {
    if (!loading && !createBookingLoading) {
      const campaignId = getCampaignIdCookie()

      const confirmBookingEvent = {
        eventName: `Confirm booking`,
        type: TrackingEventTypes.ADOBE,
        data: {
          event: {
            name: EventNames.click,
            category: 'cinch_servicing',
            action: 'booking_confirmed',
            label: 'make_a_booking',
            type: serviceOptionsSelectedNames,
            value: serviceOptionsSelectedTotalPrice,
          },
        },
      }
      trackEvent(confirmBookingEvent)

      datadogRum.addAction('Cinch Servicing: Summary Reviewed', {
        email: customerEmail,
        step,
        page: window.location.pathname,
        serviceOptionsSelectedNames,
        serviceOptionsSelectedTotalPrice,
        appointmentType,
        store,
      })

      canBookAppointment
        ? await bookAppointment({ ...context, vin: 'VIN123' }, campaignId)
        : await sendServicingEnquiry(context, campaignId)
    }
  }

  if (loading || createBookingLoading) {
    return (
      <div className={styles.loadingContainer}>
        <div>
          <LoadingSpinner
            size={64}
            foregroundCircleColor={
              designTokens.customProperties.ds.color.vividViolet
            }
          />
          <Image
            src={CinchServicingLogo}
            alt="cinch servicing logo"
            width="271"
            height="64"
            className={styles.logo}
          />
          <Text fontWeight="bold" fontSizeFixed="3xl">
            Submitting your enquiry...
          </Text>
        </div>
      </div>
    )
  }

  const displayBookingDateTime =
    (!canBookAppointment && appointmentType === 'Drop and go') ||
    canBookAppointment
  return (
    <CarMaintenanceStepWrapper
      title={canBookAppointment ? 'Your booking ' : 'Your enquiry'}
      description="Please double-check the details below."
      onContinue={handleContinue}
      showContinueButton={!loading}
      buttonText={'Submit'}
    >
      {(error || createBookingError) && (
        <Alert type="error" className={styles.enquiryError}>
          We were unable to process your enquiry. Please submit and try again.
        </Alert>
      )}
      <div className={styles.summaryOptions}>
        <SummaryItem
          title="Your car"
          onEdit={() => dispatch({ type: 'SET_STEP', step: 2 })}
        >
          <Text
            fontWeight="semibold"
            fontSizeFixed="m"
            data-testid="car-summary"
          >
            {vehicleMake} {vehicleModel}{' '}
            <span className={styles.vrmBox}>{vrm}</span>
          </Text>
        </SummaryItem>

        <SummaryItem
          title="Store"
          onEdit={() => dispatch({ type: 'SET_STEP', step: 1 })}
        >
          <Text
            fontWeight="semibold"
            fontSizeFixed="m"
            data-testid="store-summary"
          >
            {store}
          </Text>
        </SummaryItem>

        <SummaryItem
          title="Service options"
          onEdit={() => dispatch({ type: 'SET_STEP', step: 3 })}
        >
          <Text
            fontWeight="semibold"
            fontSizeFixed="m"
            data-testid="service-options-summary"
          >
            {serviceOptionsSelectedNames}
          </Text>
          <Text
            fontWeight="semibold"
            fontSizeFixed="m"
            data-testid="service-options-price"
          >
            {serviceOptionsSelectedTotalPrice}
          </Text>
        </SummaryItem>

        <SummaryItem
          title={
            canBookAppointment
              ? 'Appointment date and time'
              : 'Preferred appointment option'
          }
          onEdit={() => dispatch({ type: 'SET_STEP', step: 4 })}
        >
          <Text
            fontWeight="semibold"
            fontSizeFixed="m"
            data-testid="appointment-option"
          >
            {appointmentType}
          </Text>
          {displayBookingDateTime && bookingDate && (
            <Text
              fontWeight="semibold"
              fontSizeFixed="m"
              data-testid="appointment-date"
            >
              {format(new Date(bookingDate), 'ccc dd MMM yyyy')}
            </Text>
          )}
          {displayBookingDateTime && bookingTime && (
            <Text
              fontWeight="semibold"
              fontSizeFixed="m"
              data-testid="appointment-time"
            >
              {bookingTime}
            </Text>
          )}
        </SummaryItem>

        <SummaryItem
          title="Booking notes"
          onEdit={() => dispatch({ type: 'SET_STEP', step: 3 })}
        >
          <Text
            fontWeight="semibold"
            fontSizeFixed="m"
            data-testid="booking-notes-summary"
          >
            {truncateNotes(notes || '')}
          </Text>
        </SummaryItem>

        <SummaryItem
          title="Contact details"
          onEdit={() => dispatch({ type: 'SET_STEP', step: 5 })}
        >
          <Text
            fontWeight="semibold"
            fontSizeFixed="m"
            data-testid="customer-name-summary"
          >
            {`${
              customerTitle ? `${customerTitle} ` : ''
            }${customerFirstName} ${customerSurname}`}
          </Text>
          <Text
            fontWeight="semibold"
            fontSizeFixed="m"
            data-testid="customer-email-summary"
          >
            {customerEmail}
          </Text>
          <Text
            fontWeight="semibold"
            fontSizeFixed="m"
            data-testid="mobile-number-summary"
          >
            {customerPhone}
          </Text>
          {canBookAppointment && (
            <Text
              fontWeight="semibold"
              fontSizeFixed="m"
              data-testid="customer-address-line-1-summary"
            >
              {customerAddress?.addressLine1}
            </Text>
          )}
          {canBookAppointment && customerAddress?.addressLine2 && (
            <Text
              fontWeight="semibold"
              fontSizeFixed="m"
              data-testid="customer-address-line-2-summary"
            >
              {customerAddress?.addressLine2}
            </Text>
          )}
          {canBookAppointment && (
            <Text
              fontWeight="semibold"
              fontSizeFixed="m"
              data-testid="customer-address-town-summary"
            >
              {`${customerAddress?.postTown}, ${customerAddress?.county}`.replace(
                /, $/,
                '',
              )}
            </Text>
          )}
          <Text
            fontWeight="semibold"
            fontSizeFixed="m"
            data-testid="customer-post-code-summary"
          >
            {customerAddress ? customerAddress.postcode : postcode}
          </Text>
          {!canBookAppointment && (
            <Text
              fontWeight="semibold"
              fontSizeFixed="m"
              data-testid="best-time-to-reach-summary"
            >
              {`Best time to reach you - ${contactTimePreference}`}
            </Text>
          )}
        </SummaryItem>
      </div>

      <Text
        className={styles.confirmationText}
        fontSizeFixed="m"
        fontWeight="light"
      >
        By submitting the form you confirm you have read and agree to the {}
        <TextLink
          href="/cinch-servicing/cinchservicing_at_marshall_terms.pdf"
          target="_blank"
          rel="nofollow noreferrer"
        >
          terms and conditions.
        </TextLink>
        {} Cinch will share your details with Marshall Motor Group Limited who
        will call you back to arrange your appointment. Marshall will use your
        details in accordance with their {}
        <TextLink
          href="https://www.marshall.co.uk/site/privacy-notice/"
          target="_blank"
          rel="nofollow noreferrer"
        >
          privacy notice.
        </TextLink>
      </Text>
    </CarMaintenanceStepWrapper>
  )
}

const truncateNotes = (str: string, maxLength = 140) =>
  str.length > maxLength ? str.slice(0, maxLength) + '...' : str
